import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Crown, Gift, User, Sparkles, Building2, ArrowLeft } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import GoogleLoginButton from "@/components/GoogleLoginButton";
import { MX_STATE_NAMES, citiesForState } from "@/lib/mexicoLocations";

/**
 * Floating role selector that appears before any form fields.
 * The user MUST pick one of the three account types to continue.
 */
function RoleModal({ onSelect }) {
  const options = [
    {
      id: "visitor",
      title: "VISITANTE",
      desc: "Quiero explorar el directorio y contactar musas.",
      Icon: User,
    },
    {
      id: "companion",
      title: "MUSA",
      desc: "Quiero crear mi perfil y aparecer en la galería.",
      Icon: Sparkles,
    },
    {
      id: "agency",
      title: "AGENCIA",
      desc: "Manejo varias musas y necesito registrarlas bajo mi marca.",
      Icon: Building2,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8 fade-up"
      style={{ background: "rgba(8,8,10,0.92)", backdropFilter: "blur(16px)" }}
      data-testid="role-modal"
    >
      <div
        className="w-full max-w-3xl p-6 md:p-10"
        style={{
          background: "var(--bg-elevated)",
          border: "1px solid var(--border-accent)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
        }}
      >
        <div className="overline mb-3" style={{ color: "var(--brand-primary)" }}>
          PK2 Scarlett · Crear cuenta
        </div>
        <h2
          className="font-serif text-3xl md:text-4xl font-light mb-2"
          style={{ color: "var(--text-primary)" }}
        >
          ¿Cómo quieres <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>registrarte</em>?
        </h2>
        <p className="text-xs mb-6" style={{ color: "var(--text-muted)" }}>
          Elige el tipo de cuenta para mostrarte solo los campos que necesitas.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {options.map(({ id, title, desc, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => onSelect(id)}
              className="p-6 text-left transition-all group"
              data-testid={`role-modal-${id}`}
              style={{
                background: "var(--bg-surface)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "2px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--brand-primary)";
                e.currentTarget.style.background = "var(--bg-elevated)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--border-subtle)";
                e.currentTarget.style.background = "var(--bg-surface)";
              }}
            >
              <Icon size={32} strokeWidth={1.2} color="var(--brand-primary)" className="mb-4" />
              <div
                className="font-serif text-xl tracking-wide mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                {title}
              </div>
              <p className="text-xs leading-relaxed" style={{ color: "var(--text-muted)" }}>
                {desc}
              </p>
              <span
                className="inline-block mt-4 text-[10px] uppercase tracking-[0.25em]"
                style={{ color: "var(--brand-primary)" }}
              >
                Continuar →
              </span>
            </button>
          ))}
        </div>

        <div className="mt-6 text-xs text-center" style={{ color: "var(--text-secondary)" }}>
          ¿Ya tienes cuenta?{" "}
          <Link
            to="/acceder"
            className="hover:underline"
            style={{ color: "var(--brand-primary)" }}
            data-testid="role-modal-go-login"
          >
            Accede aquí
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = ["companion", "agency", "visitor"].includes(params.get("role"))
    ? params.get("role")
    : null;

  // `role === null` => show the floating role modal first.
  const [role, setRole] = useState(initialRole);
  const showRoleModal = role === null;

  const [agencyName, setAgencyName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [displayName, setDisplayName] = useState(""); // "Subnombre" for musa, "Nombre" for visitor, "Nombre de Agencia" mirrored for agency
  const [realName, setRealName] = useState(""); // "Nombre" real para musa/agencia
  const [age, setAge] = useState("");
  const [talla, setTalla] = useState("");
  const [heightCm, setHeightCm] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [hotelId, setHotelId] = useState("");
  const [hotelName, setHotelName] = useState("");
  const [hotels, setHotels] = useState([]);
  const [telegramId, setTelegramId] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [profilePhoto, setProfilePhoto] = useState("");
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [vipPackages, setVipPackages] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [referralCode, setReferralCode] = useState(() => (params.get("ref") || "").toUpperCase());
  const [referralInfo, setReferralInfo] = useState(null);

  useEffect(() => {
    api.get("/vip/packages").then((r) => setVipPackages(r.data || [])).catch(() => {});
    api.get("/hotels").then((r) => setHotels(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => setHotels([]));
  }, []);

  const availableCities = useMemo(() => citiesForState(stateName), [stateName]);
  useEffect(() => {
    if (stateName && availableCities.length && !availableCities.includes(city)) setCity("");
  }, [stateName, availableCities, city]);

  const filteredHotels = useMemo(() => {
    if (!Array.isArray(hotels) || !hotels.length) return [];
    const list = hotels.filter((h) => {
      if (city && h.city && h.city.toLowerCase() !== city.toLowerCase()) return false;
      if (stateName && h.state && h.state.toLowerCase() !== stateName.toLowerCase()) return false;
      return true;
    });
    return list.length ? list : hotels;
  }, [hotels, city, stateName]);

  useEffect(() => {
    const code = (referralCode || "").trim().toUpperCase();
    if (code.length !== 8) { setReferralInfo(null); return; }
    api.get(`/referrals/validate/${code}`).then((r) => setReferralInfo(r.data)).catch(() => setReferralInfo(null));
  }, [referralCode]);

  const isMusa = role === "companion";
  const isAgency = role === "agency";
  const isVisitor = role === "visitor";
  const isContentCreator = isMusa || isAgency;
  const specialCharRe = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/;

  const resetRole = () => {
    setRole(null);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!acceptedTerms) {
      setError("Debes confirmar que eres mayor de 18 años y aceptar los Términos.");
      return;
    }
    if (isContentCreator) {
      if (!realName.trim()) {
        setError(isAgency
          ? "Ingresa el nombre del responsable de la agencia."
          : "Ingresa tu nombre real (como aparece en tu INE)."
        );
        return;
      }
      if (isAgency && !agencyName.trim()) {
        setError("Ingresa el nombre de la agencia.");
        return;
      }
      if (password.length < 8) {
        setError("La contraseña debe tener al menos 8 caracteres.");
        return;
      }
      if (!specialCharRe.test(password)) {
        setError("La contraseña debe incluir al menos un carácter especial (!@#$…).");
        return;
      }
      if (password !== passwordConfirm) {
        setError("Las contraseñas no coinciden.");
        return;
      }
      if (!phone.trim() || phone.replace(/\D/g, "").length < 7) {
        setError("Ingresa un número de teléfono válido.");
        return;
      }
      if (isMusa) {
        if (!age || Number(age) < 18 || Number(age) > 80) {
          setError("La edad debe estar entre 18 y 80.");
          return;
        }
        if (!city.trim() || !stateName.trim()) {
          setError("Ingresa ciudad y estado para que tu perfil aparezca en el directorio.");
          return;
        }
        if (!profilePhoto) {
          setError("Sube tu foto de perfil principal.");
          return;
        }
      }
    } else {
      // visitor
      if (password !== passwordConfirm) {
        setError("Las contraseñas no coinciden.");
        return;
      }
    }

    setLoading(true);
    const gender = isAgency ? "otro" : isMusa ? "mujer" : "hombre";
    const affiliation = isAgency ? "agencia" : "independiente";
    const finalAgencyName = isAgency ? agencyName.trim() : "";
    // For agency we use the agency name as their public display_name too.
    const finalDisplayName = isAgency ? agencyName.trim() : displayName.trim();

    const res = await register({
      email,
      password,
      password_confirm: passwordConfirm,
      display_name: finalDisplayName,
      real_name: realName.trim(),
      role,
      gender,
      age: age ? Number(age) : 0,
      talla: talla.trim(),
      height_cm: heightCm ? Number(heightCm) : 0,
      city: city.trim(),
      state: stateName.trim(),
      hotel_id: hotelId && hotelId !== "__other__" ? hotelId : "",
      hotel_name: hotelName.trim(),
      affiliation,
      agency_name: finalAgencyName,
      phone: phone.trim(),
      whatsapp: whatsapp.trim() || phone.trim(),
      telegram_id: telegramId.trim(),
      profile_photo: isContentCreator ? profilePhoto : "",
      photos: isContentCreator ? photos : [],
      origin_url: window.location.origin,
      referral_code: (referralCode || "").trim().toUpperCase(),
    });
    setLoading(false);
    if (!res.ok) { setError(res.error); return; }

    const checkout = res.data?.checkout_url;
    if (checkout) {
      window.location.href = checkout;
      return;
    }
    nav(isContentCreator ? "/dashboard?tab=verify&new=1" : "/galeria");
  };

  // Pretty header copy per role
  const headerCopy = isVisitor
    ? { pre: "Crea tu", em: "cuenta de visitante" }
    : isMusa
    ? { pre: "Únete como", em: "Musa PK2 Scarlett" }
    : isAgency
    ? { pre: "Registra tu", em: "Agencia" }
    : { pre: "Crear", em: "cuenta" };

  return (
    <div className="min-h-[80vh] max-w-2xl mx-auto px-6 pt-20 pb-20 fade-up" data-testid="register-page">
      {showRoleModal && <RoleModal onSelect={(r) => setRole(r)} />}

      {/* Header + change-role pill */}
      {!showRoleModal && (
        <>
          <button
            type="button"
            onClick={resetRole}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] mb-4 transition-colors"
            style={{ color: "var(--text-muted)" }}
            data-testid="register-change-role"
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand-primary)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-muted)")}
          >
            <ArrowLeft size={12} /> Cambiar tipo de cuenta
          </button>

          <div className="overline mb-3">{t("auth.register_overline")}</div>
          <h1
            className="font-serif text-4xl md:text-5xl font-light mb-8"
            style={{ color: "var(--text-primary)" }}
          >
            {headerCopy.pre}{" "}
            <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>{headerCopy.em}</em>
          </h1>

          {/* Visitor — Google + free notice */}
          {isVisitor && (
            <>
              <div
                className="p-4 mb-5"
                style={{
                  background: "linear-gradient(135deg, rgba(200,16,46,0.12), rgba(212,175,55,0.08))",
                  border: "1px solid var(--brand-primary)",
                  borderRadius: "2px",
                }}
                data-testid="visitor-free-notice"
              >
                <div className="overline mb-1" style={{ color: "var(--brand-primary)" }}>
                  {t("auth.free_access")}
                </div>
                <p className="text-xs" style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
                  {t("auth.free_access_desc")}
                </p>
              </div>

              <div className="mb-5" data-testid="register-google-section">
                <GoogleLoginButton label={t("auth.register_with_google")} testid="register-google-btn" />
                <div className="flex items-center gap-3 my-4">
                  <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
                  <span className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>
                    {t("auth.or_with_email")}
                  </span>
                  <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
                </div>
              </div>
            </>
          )}

          <form onSubmit={submit} className="flex flex-col gap-4">
            {/* ───────── VISITANTE ───────── */}
            {isVisitor && (
              <>
                <div>
                  <label className="overline block mb-1.5">Nombre</label>
                  <input
                    required
                    placeholder="Tu nombre"
                    className="input-field"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    data-testid="register-name"
                  />
                </div>
                <div>
                  <label className="overline block mb-1.5">Teléfono</label>
                  <input
                    type="tel"
                    placeholder="+52 1 55 1234 5678"
                    className="input-field"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="register-phone-visitor"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </div>
                <div>
                  <label className="overline block mb-1.5">Correo electrónico</label>
                  <input
                    type="email"
                    required
                    placeholder="tu@correo.com"
                    className="input-field"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="register-email"
                  />
                </div>
                <div>
                  <label className="overline block mb-1.5">Contraseña</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    placeholder="Mín. 6 caracteres"
                    className="input-field"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="register-password"
                  />
                </div>
                <div>
                  <label className="overline block mb-1.5">Repetir contraseña</label>
                  <input
                    type="password"
                    required
                    minLength={6}
                    placeholder="Repite tu contraseña"
                    className="input-field"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    data-testid="register-password-confirm-visitor"
                  />
                  {passwordConfirm && password !== passwordConfirm && (
                    <p className="text-[10px] mt-1" style={{ color: "var(--brand-primary)" }}>
                      Las contraseñas no coinciden.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* ───────── MUSA ───────── */}
            {isMusa && (
              <>
                <div>
                  <label className="overline block mb-1.5">Subnombre (cómo apareces en la galería)</label>
                  <input
                    required
                    placeholder="Ej: Valentina, Sky, Mía…"
                    className="input-field"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    data-testid="register-name"
                  />
                  <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
                    Este nombre es público — es el que verán los visitantes en tu perfil.
                  </p>
                </div>

                <div>
                  <label className="overline block mb-1.5">Nombre real (privado · INE)</label>
                  <input
                    required
                    placeholder="Ej: María Fernanda López Hernández"
                    className="input-field"
                    value={realName}
                    onChange={(e) => setRealName(e.target.value)}
                    data-testid="register-real-name"
                  />
                  <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
                    Privado · solo lo ven los administradores para verificar identidad.
                  </p>
                </div>

                <div>
                  <label className="overline block mb-1.5">Teléfono</label>
                  <input
                    type="tel"
                    required
                    placeholder="+52 1 55 1234 5678"
                    className="input-field"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="register-phone"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">WhatsApp</label>
                  <input
                    type="tel"
                    placeholder="+52 1 55 1234 5678 (si es distinto al teléfono)"
                    className="input-field"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    data-testid="register-whatsapp"
                    inputMode="tel"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">Telegram</label>
                  <input
                    placeholder="@tu_usuario_telegram"
                    className="input-field"
                    value={telegramId}
                    onChange={(e) => setTelegramId(e.target.value)}
                    data-testid="register-telegram"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">Correo electrónico</label>
                  <input
                    type="email"
                    required
                    placeholder="tu@correo.com"
                    className="input-field"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="register-email"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">Contraseña</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Mín. 8 + 1 carácter especial (!@#$…)"
                    className="input-field"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="register-password"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">Repetir contraseña</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Repite tu contraseña"
                    className="input-field"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    data-testid="register-password-confirm"
                  />
                  {passwordConfirm && password !== passwordConfirm && (
                    <p className="text-[10px] mt-1" style={{ color: "var(--brand-primary)" }}>
                      Las contraseñas no coinciden.
                    </p>
                  )}
                </div>

                {/* Datos extra de musa */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="overline block mb-1.5">Edad</label>
                    <input
                      required
                      type="number"
                      min={18}
                      max={80}
                      placeholder="24"
                      className="input-field"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      data-testid="register-age"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-1.5">Talla</label>
                    <input
                      placeholder="Ej: S, M, 32B"
                      className="input-field"
                      value={talla}
                      onChange={(e) => setTalla(e.target.value)}
                      data-testid="register-talla"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-1.5">Estatura (cm)</label>
                    <input
                      type="number"
                      min={100}
                      max={220}
                      placeholder="168"
                      className="input-field"
                      value={heightCm}
                      onChange={(e) => setHeightCm(e.target.value)}
                      data-testid="register-height"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="overline block mb-1.5">Estado</label>
                    <select
                      required
                      className="input-field"
                      value={stateName}
                      onChange={(e) => setStateName(e.target.value)}
                      data-testid="register-state"
                    >
                      <option value="">— Selecciona un estado —</option>
                      {MX_STATE_NAMES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-1.5">Ciudad / Municipio</label>
                    {availableCities.length > 0 ? (
                      <>
                        <input
                          list="city-options"
                          required
                          placeholder={stateName ? `Selecciona o escribe (${availableCities.length} sugerencias)` : "Primero elige un estado"}
                          className="input-field"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          disabled={!stateName}
                          data-testid="register-city"
                          autoComplete="off"
                        />
                        <datalist id="city-options">
                          {availableCities.map((c) => <option key={c} value={c} />)}
                        </datalist>
                      </>
                    ) : (
                      <input
                        required
                        placeholder={stateName ? "Escribe tu ciudad" : "Primero elige un estado"}
                        className="input-field"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={!stateName}
                        data-testid="register-city"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="overline block mb-1.5">Hotel donde te encuentras (opcional)</label>
                  <select
                    className="input-field"
                    value={hotelId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setHotelId(id);
                      if (id === "__other__") setHotelName("");
                      else if (id) {
                        const h = filteredHotels.find((x) => x.id === id);
                        setHotelName(h?.name || "");
                      } else setHotelName("");
                    }}
                    data-testid="register-hotel-select"
                  >
                    <option value="">— Sin hotel / a domicilio —</option>
                    {filteredHotels.map((h) => (
                      <option key={h.id} value={h.id}>{h.name} · {h.city}, {h.state}</option>
                    ))}
                    <option value="__other__">+ Otro hotel (escribe el nombre)</option>
                  </select>
                  {hotelId === "__other__" && (
                    <input
                      className="input-field mt-2"
                      placeholder="Nombre del hotel"
                      value={hotelName}
                      onChange={(e) => setHotelName(e.target.value)}
                      data-testid="register-hotel-other"
                    />
                  )}
                </div>
              </>
            )}

            {/* ───────── AGENCIA ───────── */}
            {isAgency && (
              <>
                <div>
                  <label className="overline block mb-1.5">Nombre del responsable</label>
                  <input
                    required
                    placeholder="Tu nombre completo"
                    className="input-field"
                    value={realName}
                    onChange={(e) => setRealName(e.target.value)}
                    data-testid="register-real-name"
                  />
                  <p className="text-[10px] mt-1" style={{ color: "var(--text-muted)" }}>
                    Privado · solo lo ven los administradores.
                  </p>
                </div>

                <div>
                  <label className="overline block mb-1.5">Nombre de la agencia</label>
                  <input
                    required
                    placeholder="Ej: Agencia Lunaria"
                    className="input-field"
                    value={agencyName}
                    onChange={(e) => setAgencyName(e.target.value)}
                    data-testid="agency-name-input"
                  />
                  <p
                    className="text-[10px] mt-1"
                    style={{ color: "var(--text-muted)", lineHeight: 1.5 }}
                  >
                    Este nombre aparecerá <strong style={{ color: "var(--brand-primary)" }}>debajo de cada perfil</strong> que registres.
                    Sin límite de registros. Para eliminar o bloquear un perfil deberás contactar al administrador.
                    Cada perfil registrado pasa por verificación del administrador.
                  </p>
                </div>

                <div>
                  <label className="overline block mb-1.5">Teléfono</label>
                  <input
                    type="tel"
                    required
                    placeholder="+52 1 55 1234 5678"
                    className="input-field"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    data-testid="register-phone"
                    inputMode="tel"
                    autoComplete="tel"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">WhatsApp</label>
                  <input
                    type="tel"
                    placeholder="+52 1 55 1234 5678"
                    className="input-field"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    data-testid="register-whatsapp"
                    inputMode="tel"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">Telegram</label>
                  <input
                    placeholder="@tu_usuario_telegram"
                    className="input-field"
                    value={telegramId}
                    onChange={(e) => setTelegramId(e.target.value)}
                    data-testid="register-telegram"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">Correo electrónico</label>
                  <input
                    type="email"
                    required
                    placeholder="contacto@miagencia.com"
                    className="input-field"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="register-email"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">Contraseña</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Mín. 8 + 1 carácter especial (!@#$…)"
                    className="input-field"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="register-password"
                  />
                </div>

                <div>
                  <label className="overline block mb-1.5">Repetir contraseña</label>
                  <input
                    type="password"
                    required
                    minLength={8}
                    placeholder="Repite tu contraseña"
                    className="input-field"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    data-testid="register-password-confirm"
                  />
                  {passwordConfirm && password !== passwordConfirm && (
                    <p className="text-[10px] mt-1" style={{ color: "var(--brand-primary)" }}>
                      Las contraseñas no coinciden.
                    </p>
                  )}
                </div>

                {/* Datos extra opcionales para agencia */}
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="overline block mb-1.5">Edad (opcional)</label>
                    <input
                      type="number"
                      min={18}
                      max={80}
                      placeholder="—"
                      className="input-field"
                      value={age}
                      onChange={(e) => setAge(e.target.value)}
                      data-testid="register-age"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-1.5">Talla (opcional)</label>
                    <input
                      placeholder="—"
                      className="input-field"
                      value={talla}
                      onChange={(e) => setTalla(e.target.value)}
                      data-testid="register-talla"
                    />
                  </div>
                  <div>
                    <label className="overline block mb-1.5">Estatura (opcional)</label>
                    <input
                      type="number"
                      min={100}
                      max={220}
                      placeholder="—"
                      className="input-field"
                      value={heightCm}
                      onChange={(e) => setHeightCm(e.target.value)}
                      data-testid="register-height"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="overline block mb-1.5">Estado (opcional)</label>
                    <select
                      className="input-field"
                      value={stateName}
                      onChange={(e) => setStateName(e.target.value)}
                      data-testid="register-state"
                    >
                      <option value="">— Selecciona un estado —</option>
                      {MX_STATE_NAMES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-1.5">Ciudad (opcional)</label>
                    {availableCities.length > 0 ? (
                      <>
                        <input
                          list="city-options-agency"
                          placeholder={stateName ? "Selecciona o escribe" : "Primero elige un estado"}
                          className="input-field"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          disabled={!stateName}
                          data-testid="register-city"
                          autoComplete="off"
                        />
                        <datalist id="city-options-agency">
                          {availableCities.map((c) => <option key={c} value={c} />)}
                        </datalist>
                      </>
                    ) : (
                      <input
                        placeholder={stateName ? "Escribe la ciudad" : "Primero elige un estado"}
                        className="input-field"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={!stateName}
                        data-testid="register-city"
                      />
                    )}
                  </div>
                </div>

                <div>
                  <label className="overline block mb-1.5">Hotel base (opcional)</label>
                  <select
                    className="input-field"
                    value={hotelId}
                    onChange={(e) => {
                      const id = e.target.value;
                      setHotelId(id);
                      if (id === "__other__") setHotelName("");
                      else if (id) {
                        const h = filteredHotels.find((x) => x.id === id);
                        setHotelName(h?.name || "");
                      } else setHotelName("");
                    }}
                    data-testid="register-hotel-select"
                  >
                    <option value="">— Sin hotel base —</option>
                    {filteredHotels.map((h) => (
                      <option key={h.id} value={h.id}>{h.name} · {h.city}, {h.state}</option>
                    ))}
                    <option value="__other__">+ Otro hotel</option>
                  </select>
                  {hotelId === "__other__" && (
                    <input
                      className="input-field mt-2"
                      placeholder="Nombre del hotel"
                      value={hotelName}
                      onChange={(e) => setHotelName(e.target.value)}
                      data-testid="register-hotel-other"
                    />
                  )}
                </div>
              </>
            )}

            {/* ───────── FOTOS (musa + agencia) ───────── */}
            {isContentCreator && (
              <div className="mt-2 flex flex-col gap-5" data-testid="register-photos-section">
                <div>
                  <label className="overline block mb-1.5">
                    Foto de perfil {isMusa && <span style={{ color: "var(--brand-primary)" }}>*</span>}
                  </label>
                  <p className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>
                    La foto principal — se sube y actualiza automáticamente al seleccionar el archivo.
                  </p>
                  <PhotoUploader
                    folder="profiles/"
                    multiple={false}
                    accept="image"
                    testid="register-profile-photo-uploader"
                    anonymous={true}
                    onUploaded={(urls) => {
                      const u = Array.isArray(urls) ? urls[0] : urls;
                      if (u) setProfilePhoto(u);
                    }}
                  />
                  {profilePhoto && (
                    <div
                      className="flex items-center gap-3 mt-2 p-2"
                      style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}
                    >
                      <img
                        src={profilePhoto}
                        alt="Perfil"
                        style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 2 }}
                      />
                      <div
                        className="flex-1 truncate text-[11px] font-mono"
                        style={{ color: "var(--text-secondary)" }}
                        title={profilePhoto}
                        data-testid="register-profile-photo-url"
                      >
                        {profilePhoto}
                      </div>
                      <button
                        type="button"
                        onClick={() => setProfilePhoto("")}
                        className="text-[10px] px-2 py-1"
                        style={{ color: "var(--brand-primary)", border: "1px solid var(--brand-primary)" }}
                        data-testid="register-profile-photo-clear"
                      >
                        Quitar
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="overline block mb-1.5">Fotos de galería (mín. 3 para musa)</label>
                  <p className="text-[11px] mb-2" style={{ color: "var(--text-muted)" }}>
                    Súbelas y se publicarán al ser aprobadas por un administrador.
                  </p>
                  <PhotoUploader
                    folder="profiles/"
                    multiple={true}
                    accept="image"
                    testid="register-photo-uploader"
                    anonymous={true}
                    onUploaded={(urls) =>
                      setPhotos((prev) => [...prev, ...(Array.isArray(urls) ? urls : [urls])].filter(Boolean))
                    }
                  />
                  {photos.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mt-2" data-testid="register-gallery-list">
                      {photos.map((u, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-2 p-2"
                          style={{ background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}
                        >
                          <img
                            src={u}
                            alt={`Foto ${i + 1}`}
                            style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 2 }}
                          />
                          <div
                            className="flex-1 truncate text-[10px] font-mono"
                            style={{ color: "var(--text-secondary)" }}
                            title={u}
                          >
                            {u}
                          </div>
                          <button
                            type="button"
                            onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}
                            className="text-[10px] px-1.5 py-0.5"
                            style={{ color: "var(--brand-primary)" }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {isMusa && (
                    <p
                      className="text-[11px] mt-2"
                      data-testid="register-photos-count"
                      style={{ color: photos.length >= 3 ? "var(--text-secondary)" : "var(--brand-primary)" }}
                    >
                      {photos.length}/3+ fotos.{" "}
                      {photos.length < 3 ? "Sube al menos 3 fotos." : "✓ Suficientes."}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ───────── Términos ───────── */}
            <label
              className="flex items-start gap-3 p-4 cursor-pointer"
              style={{
                background: "var(--bg-elevated)",
                border: `1px solid ${acceptedTerms ? "var(--brand-primary)" : "var(--border-subtle)"}`,
                transition: "border-color 0.2s",
              }}
              data-testid="terms-accept-wrap"
            >
              <input
                type="checkbox"
                className="mt-1"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                data-testid="terms-accept-checkbox"
                style={{ accentColor: "var(--brand-primary)", width: 18, height: 18, flexShrink: 0 }}
              />
              <span className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                Confirmo que tengo <strong style={{ color: "var(--brand-primary)" }}>18 años o más</strong>, entiendo que esta plataforma contiene contenido para adultos y acepto los{" "}
                <Link to="/terminos" target="_blank" style={{ color: "var(--brand-primary)", textDecoration: "underline" }} data-testid="terms-link">
                  Términos y Condiciones
                </Link>{" "}
                y la{" "}
                <Link to="/privacidad" target="_blank" style={{ color: "var(--brand-primary)", textDecoration: "underline" }} data-testid="privacy-link">
                  Política de Privacidad
                </Link>
                .
              </span>
            </label>

            {error && (
              <p className="text-xs" style={{ color: "#ef6d6d" }} data-testid="register-error">
                {error}
              </p>
            )}

            {/* Referral code */}
            <div className="mt-2" data-testid="register-referral-section">
              <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--text-muted)" }}>
                {t("auth.referral_title")}
              </label>
              <input
                type="text"
                maxLength={8}
                className="input-field uppercase tracking-[0.3em]"
                placeholder={t("auth.referral_placeholder")}
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                data-testid="register-referral-code"
              />
              {referralInfo && referralInfo.valid && (
                <p className="text-xs mt-1" style={{ color: "var(--vip-gold)" }} data-testid="register-referral-valid">
                  {t("auth.referral_valid", { name: referralInfo.referrer_name })}
                </p>
              )}
              {referralInfo && !referralInfo.valid && referralCode.length === 8 && (
                <p className="text-xs mt-1" style={{ color: "#ef6d6d" }} data-testid="register-referral-invalid">
                  {t("auth.referral_invalid")}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="btn-primary mt-2"
              data-testid="register-submit"
            >
              {loading ? t("auth.submit_processing") : t("auth.submit_register")}
            </button>
          </form>

          {/* PRICING (musa/agency) */}
          {isContentCreator && vipPackages.length > 0 && (
            <section className="mt-12" data-testid="register-pricing">
              <div
                className="p-5 mb-6 text-center"
                style={{
                  background: "linear-gradient(135deg, rgba(212,175,55,0.14), rgba(200,16,46,0.12))",
                  border: "1px solid var(--vip-gold)",
                  borderRadius: "2px",
                }}
                data-testid="pricing-free-week-banner"
              >
                <Gift size={24} strokeWidth={1.3} color="var(--vip-gold)" className="mx-auto mb-2" />
                <h2 className="font-serif text-2xl mb-2" style={{ color: "var(--vip-gold)" }}>
                  Primera semana GRATIS
                </h2>
                <p className="text-xs" style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
                  Al crear tu perfil activas automáticamente{" "}
                  <strong style={{ color: "var(--vip-gold)" }}>15 días de VIP sin costo</strong>.
                  Después, si deseas seguir como VIP elige uno de los planes de abajo.
                  <br />
                  <span style={{ color: "var(--text-muted)" }}>
                    No se cobra durante la primera semana · Cancela cuando quieras.
                  </span>
                </p>
              </div>

              <div className="overline mb-3" style={{ color: "var(--brand-primary)" }}>
                Planes VIP después de la semana gratis
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="pricing-grid">
                {vipPackages.map((p) => (
                  <Link
                    key={p.id}
                    to={`/vip?plan=${p.id}`}
                    className="p-5 block transition-all"
                    style={{
                      background: "var(--bg-surface)",
                      border: "1px solid var(--border-accent)",
                      borderRadius: "2px",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = "var(--brand-primary)";
                      e.currentTarget.style.background = "var(--bg-elevated)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = "var(--border-accent)";
                      e.currentTarget.style.background = "var(--bg-surface)";
                    }}
                    data-testid={`pricing-card-${p.id}`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Crown size={12} color="var(--vip-gold)" />
                      <span className="overline">{p.label}</span>
                    </div>
                    <div className="font-serif text-3xl font-light" style={{ color: "var(--vip-gold)" }}>
                      ${p.amount.toLocaleString("es-MX")}
                      <span className="text-sm ml-1" style={{ color: "var(--text-secondary)" }}>
                        {p.currency.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                      {p.days} {p.days === 1 ? "día" : "días"} de visibilidad VIP
                    </p>
                    <span
                      className="inline-block mt-4 text-xs uppercase tracking-[0.2em]"
                      style={{ color: "var(--brand-primary)", fontWeight: 600 }}
                      data-testid={`pricing-cta-${p.id}`}
                    >
                      Activar ahora →
                    </span>
                  </Link>
                ))}
              </div>

              <p className="text-xs mt-5 text-center" style={{ color: "var(--text-muted)" }}>
                Pagos procesados por <strong style={{ color: "var(--text-secondary)" }}>Mercado Pago</strong> ·
                Acepta tarjeta, SPEI y OXXO · Cancelación en cualquier momento
              </p>
            </section>
          )}

          <div className="mt-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
            ¿Ya tienes cuenta?{" "}
            <Link to="/login" className="hover:underline" style={{ color: "var(--brand-primary)" }}>
              Accede aquí
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
